/**
 * AI Verification — Lead Command Center demo view.
 *
 * Shows past verification runs and lets the demo user run a NEW simulated
 * verification against any lead. The "call" is a deterministic server-side
 * simulation — no telephony, no OpenAI, no outbound anything.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PhoneOutgoing, ShieldCheck } from 'lucide-react';
import { leadsApi } from '@/lib/api';
import { lccApi, type LccVerification } from '@/lib/lcc-api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  formatLccDateTime,
  formatLccLabel,
  LccErrorCard,
  LccLoadingCard,
  LccPageHeader,
} from './lcc-shared';

export function AiVerificationView() {
  const queryClient = useQueryClient();
  const [selectedLeadId, setSelectedLeadId] = useState('');

  const verifications = useQuery({
    queryKey: ['lcc', 'verifications'],
    queryFn: () => lccApi.verifications(),
  });
  const leads = useQuery({
    queryKey: ['leads', 'all'],
    queryFn: () => leadsApi.list({ limit: 500 }),
  });

  const runMutation = useMutation({
    mutationFn: (leadId: string) => lccApi.verifyLead(leadId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['lcc'] });
      void queryClient.invalidateQueries({ queryKey: ['leads'] });
      void queryClient.invalidateQueries({ queryKey: ['activity'] });
    },
  });

  return (
    <div className="mx-auto flex w-full max-w-[1000px] flex-col gap-4">
      <LccPageHeader
        title="AI Verification"
        blurb="Opted-in inbound leads get a verification call from the AI assistant before a human spends time on them. In this demo the call is fully simulated server-side — the result, transcript, and status change are real data in Supabase, but nobody is contacted."
      />

      <Card className="flex flex-col gap-3 p-4">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary">
            <PhoneOutgoing className="h-4 w-4" aria-hidden />
          </span>
          <div>
            <h2 className="text-[14px] font-semibold leading-tight">Run a simulated verification</h2>
            <p className="text-[12px] text-muted-foreground">
              Pick a lead — the simulated result is written to its timeline and, when verified,
              the lead moves to the Verified stage.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={selectedLeadId} onValueChange={setSelectedLeadId}>
            <SelectTrigger className="h-9 w-full sm:w-[380px]">
              <SelectValue placeholder="Choose a lead…" />
            </SelectTrigger>
            <SelectContent>
              {(leads.data?.items ?? []).map((lead) => (
                <SelectItem key={lead.id} value={lead.id}>
                  {lead.projectName} — {lead.status}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            type="button"
            size="sm"
            disabled={!selectedLeadId || runMutation.isPending}
            onClick={() => runMutation.mutate(selectedLeadId)}
          >
            {runMutation.isPending ? 'Running…' : 'Run verification'}
          </Button>
        </div>
        {runMutation.error != null && (
          <p className="text-[12px] text-destructive">
            {runMutation.error instanceof Error ? runMutation.error.message : 'Request failed'}
          </p>
        )}
        {runMutation.data && (
          <p className="text-[12px] text-muted-foreground">
            Latest run: <span className="font-medium">{formatLccLabel(runMutation.data.result)}</span>
            {runMutation.data.score !== undefined && <> · score {runMutation.data.score}</>} — see
            the list below.
          </p>
        )}
      </Card>

      {verifications.isLoading && <LccLoadingCard />}
      {verifications.error != null && (
        <LccErrorCard title="Couldn't load verifications" error={verifications.error} />
      )}
      {!verifications.isLoading && verifications.error == null && (
        <Card className="overflow-hidden">
          <ul className="divide-y divide-border">
            {(verifications.data?.items ?? []).length === 0 && (
              <li className="px-4 py-12 text-center text-sm text-muted-foreground">
                <div className="flex flex-col items-center gap-2">
                  <ShieldCheck className="h-6 w-6 text-muted-foreground/50" aria-hidden />
                  <span>No verification runs yet — try one above.</span>
                </div>
              </li>
            )}
            {(verifications.data?.items ?? []).map((run) => (
              <VerificationRow key={run.id} run={run} />
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

function VerificationRow({ run }: { run: LccVerification }) {
  return (
    <li className="flex items-start gap-3 px-4 py-3">
      <div className="mt-0.5">
        <Badge
          variant={
            run.result === 'verified'
              ? 'success'
              : run.result === 'failed'
                ? 'destructive'
                : 'warning'
          }
        >
          {formatLccLabel(run.result)}
        </Badge>
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium">
          {run.leadTitle ? (
            <Link className="underline-offset-2 hover:underline" to={`/demo/lcc/leads/${run.leadId}`}>
              {run.leadTitle}
            </Link>
          ) : (
            run.leadId
          )}
        </div>
        {run.summary && <div className="mt-0.5 text-[12px] text-muted-foreground">{run.summary}</div>}
        {run.transcript && (
          <details className="mt-1">
            <summary className="cursor-pointer text-[12px] text-muted-foreground underline-offset-2 hover:underline">
              Transcript (simulated)
            </summary>
            <pre className="mt-1 whitespace-pre-wrap rounded-md bg-muted/50 p-2 text-[12px] leading-snug">
              {run.transcript}
            </pre>
          </details>
        )}
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[12px] text-muted-foreground">
          <span className="capitalize">{formatLccLabel(run.verificationType)}</span>
          {run.score !== undefined && <span>Score {run.score}</span>}
        </div>
      </div>
      <time className="whitespace-nowrap text-[11px] text-muted-foreground">
        {formatLccDateTime(run.createdAt)}
      </time>
    </li>
  );
}
